import React, { useState, useEffect, useContext } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import Layout from '../components/Layout'
import { AuthContext } from '../App'
import axios from 'axios'
import { toast } from 'sonner'
import { Save, Eye, Loader2, Mic, Plus } from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import AdvancedImageViewer from '../components/AdvancedImageViewer'

const VerifySurgical = () => {
  const { API } = useContext(AuthContext)
  const navigate = useNavigate()
  const location = useLocation()
  const [surgicalItems, setSurgicalItems] = useState([])
  const [setName, setSetName] = useState('')
  const [imageBase64, setImageBase64] = useState('')
  const [fileType, setFileType] = useState('image')
  const [saving, setSaving] = useState(false)
  const [uomList, setUomList] = useState([])
  const [invalidFields, setInvalidFields] = useState([])
  const [showImageViewer, setShowImageViewer] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [editingSetId, setEditingSetId] = useState(null)
  const [listeningIndex, setListeningIndex] = useState(null)
  const hasProcessedData = React.useRef(false)

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasProcessedData.current) return
    
    // React Router v7 fix: Try sessionStorage if location.state is empty
    let surgicalData = location.state
    
    if (!surgicalData?.surgical_items) {
      const storedData = sessionStorage.getItem('surgicalOcrNavigationData')
      if (storedData) {
        try {
          surgicalData = JSON.parse(storedData)
        } catch (e) {
          console.error('Failed to parse sessionStorage data:', e)
        }
      }
    }
    
    if (!surgicalData?.surgical_items) {
      navigate('/upload-surgical')
      return
    }
    
    // Mark data as processed and clear sessionStorage
    hasProcessedData.current = true
    sessionStorage.removeItem('surgicalOcrNavigationData')

    // Process surgical items
    const processedItems = surgicalData.surgical_items.map(item => {
      // Format MRP to 2 decimals
      let formattedMrp = item.mrp || ''
      if (formattedMrp) {
        const numValue = parseFloat(formattedMrp)
        if (!isNaN(numValue)) {
          formattedMrp = numValue.toFixed(2)
        }
      }
      
      return {
        ...item,
        mrp: formattedMrp,
        uom: item.uom || 'Unit',
        is_new_in_master: item.is_new_in_master || false
      }
    })

    setSurgicalItems(processedItems)
    setImageBase64(surgicalData.imageBase64)
    setFileType(surgicalData.fileType || 'image')
    
    // Check if we're editing an existing set
    if (surgicalData.isEditing) {
      setIsEditing(true)
      setEditingSetId(surgicalData.setId)
      setSetName(surgicalData.setName || '')
    }
    
    fetchConfig()
  }, [])

  const fetchConfig = async () => {
    try {
      const uomRes = await axios.get(`${API}/config/item-uom`)
      setUomList(uomRes.data.uom_list)
    } catch (error) {
      console.error('Failed to fetch config:', error)
    }
  }

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...surgicalItems]
    
    // Format MRP to 2 decimals when field is mrp
    if (field === 'mrp' && value) {
      const numValue = parseFloat(value)
      if (!isNaN(numValue)) {
        updatedItems[index][field] = numValue.toFixed(2)
      } else {
        updatedItems[index][field] = value
      }
    } else {
      updatedItems[index][field] = value
    }
    
    // If UoM changes and it's new, highlight it
    if (field === 'uom') {
      const isNewUom = !uomList.some(item => item.uom === value)
      if (isNewUom && value) {
        // Add to UoM list dynamically
        setUomList(prev => [...prev, { uom: value, qty_calculation: '1' }])
        toast.info(`New UoM "${value}" added to the list`, { duration: 3000 })
      }
    }
    
    setSurgicalItems(updatedItems)
    
    // Clear validation for this field
    setInvalidFields(prev => prev.filter(f => !(f.index === index && f.field === field)))
  }

  const handleVoiceInput = (index) => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      toast.error('Voice input not supported in this browser')
      return
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition
    const recognition = new SpeechRecognition()
    recognition.lang = 'en-US'
    recognition.continuous = false
    recognition.interimResults = false

    setListeningIndex(index)

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript
      handleItemChange(index, 'name', transcript)
      toast.success('Voice input captured!')
    }

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error)
      toast.error('Voice input failed: ' + event.error)
    }

    recognition.onend = () => {
      setListeningIndex(null)
    }

    recognition.start()
  }

  const handleAddManualItem = () => {
    const newItem = {
      name: '',
      uom: 'Unit',
      mrp: '',
      action: 'Add',
      is_new_in_master: false
    }
    setSurgicalItems([...surgicalItems, newItem])
    toast.success('New row added for manual entry')
  }

  const isFieldInvalid = (index, field) => {
    return invalidFields.some(f => f.index === index && f.field === field)
  }

  const isNewUom = (uom) => {
    if (!uom) return false
    return !uomList.some(item => item.uom.toLowerCase() === uom.toLowerCase())
  }

  const handleSave = async () => {
    if (!setName) {
      toast.error('Please enter a name for this surgical set')
      document.querySelector('[data-testid="set-name-input"]')?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      document.querySelector('[data-testid="set-name-input"]')?.focus()
      return
    }

    // Check for missing mandatory fields
    const missingFields = []
    surgicalItems.forEach((item, index) => {
      if (!item.name) missingFields.push({ index, field: 'name' })
      if (!item.uom) missingFields.push({ index, field: 'uom' })
      if (!item.mrp) missingFields.push({ index, field: 'mrp' })
    })

    if (missingFields.length > 0) {
      setInvalidFields(missingFields)
      toast.error('Fill all mandatory fields', { duration: 5000 })
      
      // Scroll to first missing field and focus
      const firstMissing = missingFields[0]
      const selector = `[data-testid="surgical-${firstMissing.field}-${firstMissing.index}"]`
      const element = document.querySelector(selector)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })
        setTimeout(() => {
          const input = element.querySelector('input, select, button')
          input?.focus()
          input?.click()
        }, 500)
      }
      return
    }

    // Clear invalid fields if validation passes
    setInvalidFields([])
    setSaving(true)

    try {
      const payload = {
        set_name: setName,
        surgical_items: surgicalItems,
        image_base64: imageBase64
      }

      if (isEditing) {
        await axios.put(`${API}/surgical-sets/${editingSetId}`, payload)
        toast.success('Surgical set updated successfully!')
      } else {
        await axios.post(`${API}/surgical-sets`, payload)
        toast.success('Surgical set saved successfully!')
      }
      
      navigate('/all-surgical-sets')
    } catch (error) {
      console.error('Save error:', error)
      toast.error(error.response?.data?.detail || 'Failed to save surgical set')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteItem = (index) => {
    const updatedItems = surgicalItems.filter((_, i) => i !== index)
    setSurgicalItems(updatedItems)
    toast.success('Surgical item removed')
  }

  return (
    <Layout title="Verify Surgical Items" fullWidth={true}>
        <div className="bg-white p-4 md:p-6 min-h-screen">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <h2 className="text-xl font-semibold text-gray-900">Verify Surgical Items</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowImageViewer(true)}
            >
              <Eye className="w-4 h-4 mr-2" />
              View Challan
            </Button>
          </div>

          <div className="mb-4 flex justify-end">
            <Button onClick={handleAddManualItem} variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              Add Surgical Item
            </Button>
          </div>

        <div className="overflow-x-auto w-full">
          <table className="w-full border-collapse table-auto">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 px-2 py-2 text-left" style={{width: '50px'}}>#</th>
                <th className="border border-gray-300 px-4 py-2 text-left" style={{minWidth: '350px'}}>Surgical Item Name *</th>
                <th className="border border-gray-300 px-4 py-2 text-left" style={{width: '150px'}}>UoM *</th>
                <th className="border border-gray-300 px-4 py-2 text-left" style={{width: '120px'}}>MRP *</th>
                <th className="border border-gray-300 px-4 py-2 text-left" style={{width: '80px'}}>Action</th>
                <th className="border border-gray-300 px-2 py-2 text-center" style={{width: '100px'}}>Delete</th>
              </tr>
            </thead>
            <tbody>
              {surgicalItems.map((item, index) => (
                <tr key={index} className={item.is_new_in_master ? 'bg-yellow-50' : ''}>
                  <td className="border border-gray-300 px-2 py-2 text-center">{index + 1}</td>
                  <td 
                    className="border border-gray-300 px-4 py-2"
                    style={{
                      backgroundColor: isFieldInvalid(index, 'name') ? '#fee2e2' : 
                                       item.is_new_in_master ? '#fef3c7' : 'transparent'
                    }}
                  >
                    <div className="flex gap-2">
                      <Input
                        value={item.name || ''}
                        onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                        placeholder="Surgical item name"
                        data-testid={`surgical-name-${index}`}
                        className={isFieldInvalid(index, 'name') ? 'border-red-400' : ''}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleVoiceInput(index)}
                        data-testid={`voice-input-${index}`}
                        title="Use voice input"
                      >
                        <Mic className={`w-4 h-4 ${listeningIndex === index ? 'text-red-500 animate-pulse' : ''}`} />
                      </Button>
                    </div>
                    {item.is_new_in_master && (
                      <span className="text-xs text-orange-600 mt-1 block">New item added to master</span>
                    )}
                  </td>
                  <td 
                    className="border border-gray-300 px-4 py-2"
                    style={{
                      backgroundColor: isFieldInvalid(index, 'uom') ? '#fee2e2' : 
                                       isNewUom(item.uom) ? '#fed7aa' : 'transparent'
                    }}
                  >
                    <Select 
                      value={item.uom || ''} 
                      onValueChange={(value) => handleItemChange(index, 'uom', value)}
                    >
                      <SelectTrigger 
                        data-testid={`surgical-uom-${index}`}
                        className={isFieldInvalid(index, 'uom') ? 'border-red-400' : ''}
                      >
                        <SelectValue placeholder="Select UoM" />
                      </SelectTrigger>
                      <SelectContent>
                        {uomList.map(uomItem => (
                          <SelectItem key={uomItem.uom} value={uomItem.uom}>
                            {uomItem.uom}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {isNewUom(item.uom) && item.uom && (
                      <span className="text-xs text-orange-600 mt-1 block">New UoM</span>
                    )}
                  </td>
                  <td 
                    className="border border-gray-300 px-2 py-2"
                    style={{backgroundColor: isFieldInvalid(index, 'mrp') ? '#fee2e2' : 'transparent'}}
                  >
                    <Input
                      value={item.mrp || ''}
                      onChange={(e) => handleItemChange(index, 'mrp', e.target.value)}
                      onBlur={(e) => {
                        // Format to 2 decimals on blur
                        const val = parseFloat(e.target.value)
                        if (!isNaN(val)) {
                          handleItemChange(index, 'mrp', val.toFixed(2))
                        }
                      }}
                      placeholder="0.00"
                      type="number"
                      step="0.01"
                      data-testid={`surgical-mrp-${index}`}
                      className={`${isFieldInvalid(index, 'mrp') ? 'border-red-400' : ''} text-base md:text-sm`}
                      style={{minWidth: '100px', fontSize: '16px'}}
                    />
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    <span className="text-xs md:text-sm font-medium">Add</span>
                  </td>
                  <td className="border border-gray-300 px-2 py-2 text-center">
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDeleteItem(index)}
                      className="text-xs md:text-sm"
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Surgical Set Name *
          </label>
          <div className="flex gap-4 items-start">
            <Input
              type="text"
              value={setName}
              onChange={(e) => setSetName(e.target.value)}
              placeholder="Enter surgical set name"
              data-testid="set-name-input"
              className="flex-1 max-w-md"
            />
            <Button
              variant="outline"
              onClick={() => navigate('/upload-surgical')}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || surgicalItems.length === 0}
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {isEditing ? 'Update Set' : 'Save Set'}
                </>
              )}
            </Button>
          </div>
        </div>
        </div>

      {showImageViewer && (
        <AdvancedImageViewer
          imageBase64={imageBase64}
          fileType={fileType}
          onClose={() => setShowImageViewer(false)}
        />
      )}
    </Layout>
  )
}

export default VerifySurgical
